import { useEffect, useRef, useState } from 'react'
import { Box, FormControlLabel, FormControlLabelProps, Radio, Stack, styled } from '@mui/material'
import { WarningIcon, XIcon } from '@phosphor-icons/react'
import type { TenantImportAnswer, TenantImportQuestion, TenantImportRow } from '@einwert/valuation-api-client'
import { CardItem, tokens } from 'ComponentLibrary'
import { Typography } from 'ComponentLibrary/src/piazza'
import { TextButtonNew, TinyButton } from 'ComponentLibrary/src/piazza/buttons'
import { ContentChip } from 'ComponentLibrary/src/piazza/content/ContentChip/ContentChip'
import { TextInput } from 'ComponentLibrary/src/piazza/forms/TextInput/TextInput'

import { tenantImportCopy, type TenantImportLanguage } from './copy'

type AnswerDraft = { optionId?: string; custom: boolean; text: string }

export function TenantImportClarifications({
    language = 'de',
    questions,
    rows,
    answers,
    onChange,
    onComplete,
    onRestart,
    disabled,
    embedded = false,
}: {
    language?: TenantImportLanguage
    questions: TenantImportQuestion[]
    rows: TenantImportRow[]
    answers: TenantImportAnswer[]
    onChange: (answers: TenantImportAnswer[]) => void
    onComplete: (answers: TenantImportAnswer[]) => void
    onRestart: () => void
    disabled: boolean
    embedded?: boolean
}) {
    const copy = tenantImportCopy(language)
    const [activeId, setActiveId] = useState(questions[0]?.id)
    const [drafts, setDrafts] = useState<Record<string, AnswerDraft>>({})
    const heading = useRef<HTMLDivElement>(null)
    const index = Math.max(
        0,
        questions.findIndex((question) => question.id === activeId)
    )
    const question = questions[index]

    useEffect(() => {
        heading.current?.focus({ preventScroll: true })
    }, [question?.id])

    if (!question) {
        return null
    }
    const confirmation = question.input_type === 'confirmation'
    const affected = rows.filter((row) => row.source_ids.some((id) => question.source_ids.includes(id)))
    const references = affected
        .slice(0, 4)
        .map((row) => row.reference_number || row.source_label)
        .join(', ')
    const accepted = answers.find((item) => item.question.id === question.id)
    const recommendation = question.recommended_option_id || (question.options.length === 1 ? question.options[0].id : undefined)
    const draft = drafts[question.id] ?? {
        optionId: accepted ? accepted.selected_option_ids[0] : recommendation,
        custom: !!accepted?.text || question.input_type === 'text',
        text: accepted?.text ?? '',
    }
    const answered = draft.custom ? !!draft.text.trim() : !!draft.optionId
    const hasNext = index < questions.length - 1
    const update = (changes: Partial<AnswerDraft>) => setDrafts({ ...drafts, [question.id]: { ...draft, ...changes } })
    const accept = () => {
        const nextAnswers = [
            ...answers.filter((answer) => answer.question.id !== question.id),
            {
                question,
                selected_option_ids: !draft.custom && draft.optionId ? [draft.optionId] : [],
                text: draft.custom ? draft.text : '',
            },
        ]
        onChange(nextAnswers)
        if (hasNext) {
            setActiveId(questions[index + 1].id)
        } else {
            onComplete(nextAnswers)
        }
    }

    return (
        <CardItem
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: tokens.SizingSpacing100,
                overflow: 'hidden',
                ...(embedded && { padding: 0, border: 0, borderRadius: 0 }),
            }}
        >
            <Stack direction="row" alignItems="center" justifyContent="space-between" gap={tokens.SizingSpacing100}>
                <Box
                    ref={heading}
                    role="heading"
                    aria-level={3}
                    tabIndex={-1}
                    aria-label={`${copy.question(index + 1, questions.length)}: ${question.title}`}
                    sx={{ outline: 'none' }}
                >
                    <Stack direction="row" alignItems="center" gap={tokens.SizingSpacing100}>
                        {confirmation && (
                            <WarningIcon size={tokens.SizingIconM} color={tokens.ColorIconWarning} style={{ flexShrink: 0 }} />
                        )}
                        <Typography variant="textM" color={tokens.ColorTextBoldest} bold>
                            {question.title}
                        </Typography>
                    </Stack>
                </Box>
                <Typography variant="textS" color={tokens.ColorTextSubtle}>
                    {index + 1}/{questions.length}
                </Typography>
            </Stack>
            <Stack
                key={question.id}
                gap={tokens.SizingSpacing100}
                sx={{
                    animation: 'tenant-question-enter 180ms ease-out',
                    '@keyframes tenant-question-enter': {
                        from: { opacity: 0, transform: 'translateX(12px)' },
                        to: { opacity: 1, transform: 'translateX(0)' },
                    },
                    '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                }}
            >
                <Typography variant="textS" color={tokens.ColorTextSubtle}>
                    {question.reason}
                </Typography>
                {references && (
                    <Typography variant="textXS" color={tokens.ColorTextSubtle}>
                        {copy.tenants} {references}
                        {affected.length > 4 ? ' …' : ''}
                    </Typography>
                )}
                {confirmation ? (
                    <Stack gap={tokens.SizingSpacing050}>
                        <Typography variant="textS" bold>
                            {question.options[0].label}
                        </Typography>
                        {question.options[0].consequence && (
                            <Typography variant="textXS" color={tokens.ColorTextSubtle}>
                                {question.options[0].consequence}
                            </Typography>
                        )}
                    </Stack>
                ) : (
                    <Stack role="radiogroup" aria-label={question.title} gap={tokens.SizingSpacing100}>
                        {question.options.map((option) => (
                            <AnswerOption
                                key={option.id}
                                value={option.id}
                                checked={!draft.custom && draft.optionId === option.id}
                                disabled={disabled}
                                control={
                                    <Radio
                                        size="small"
                                        color="success"
                                        name={question.id}
                                        onChange={() => update({ optionId: option.id, custom: false })}
                                    />
                                }
                                label={
                                    <Stack gap={tokens.SizingSpacing050}>
                                        <Stack direction="row" alignItems="center" gap={tokens.SizingSpacing100}>
                                            <Typography variant="textS">{option.label}</Typography>
                                            {recommendation === option.id && (
                                                <ContentChip size="small" color="green" label={copy.recommended} />
                                            )}
                                        </Stack>
                                        {option.consequence && (
                                            <Typography variant="textXS" color={tokens.ColorTextSubtle}>
                                                {option.consequence}
                                            </Typography>
                                        )}
                                    </Stack>
                                }
                            />
                        ))}
                        <AnswerContainer
                            $disabled={disabled}
                            $checked={draft.custom}
                            direction="row"
                            alignItems="center"
                            gap={tokens.SizingSpacing150}
                            padding={tokens.SizingSpacing150}
                            onClick={disabled ? undefined : () => update({ custom: true })}
                        >
                            <Radio
                                size="small"
                                color="success"
                                name={question.id}
                                checked={draft.custom}
                                disabled={disabled}
                                slotProps={{ input: { 'aria-label': copy.customAnswer } }}
                                onChange={() => update({ custom: true })}
                            />
                            <TextInput
                                aria-label={question.title}
                                placeholder={copy.answerPlaceholder}
                                value={draft.text}
                                disabled={disabled}
                                onFocus={() => update({ custom: true })}
                                onChange={(event) => update({ text: event.target.value.slice(0, 2000) })}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter' && !event.nativeEvent.isComposing && answered) {
                                        event.preventDefault()
                                        accept()
                                    }
                                }}
                            />
                        </AnswerContainer>
                    </Stack>
                )}
            </Stack>
            <Stack direction="row" alignItems="center" justifyContent="flex-end" gap={tokens.SizingSpacing100}>
                <TinyButton label={copy.cancel} leftIcon={<XIcon />} onClick={onRestart} disabled={disabled} />
                {index > 0 && <TinyButton label={copy.back} onClick={() => setActiveId(questions[index - 1].id)} disabled={disabled} />}
                <TextButtonNew
                    label={confirmation ? copy.accept : copy.next}
                    variant="primary"
                    onClick={accept}
                    disabled={disabled || !answered}
                />
            </Stack>
        </CardItem>
    )
}

function AnswerOption(props: FormControlLabelProps) {
    return (
        <AnswerContainer $checked={props.checked} $disabled={props.disabled}>
            <FormControlLabel {...props} />
        </AnswerContainer>
    )
}

const AnswerContainer = styled(Stack, {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})<{ $checked?: boolean; $disabled?: boolean }>(({ $checked, $disabled }) => ({
    borderRadius: tokens.SizingCornerM,
    border: `${tokens.SizingBorderS} solid ${$checked ? tokens.ColorBorderSelectedDefault : tokens.ColorBorderDefault}`,
    backgroundColor: $checked ? tokens.ColorSurfaceSelectedDefault : undefined,
    '& .MuiFormControlLabel-root': {
        margin: 0,
        padding: tokens.SizingSpacing150,
        gap: tokens.SizingSpacing150,
        alignItems: 'flex-start',
    },
    '& .MuiRadio-root': { padding: 0 },
    '& .MuiFormControlLabel-label': { flex: 1, minWidth: 0 },
    ...(!$disabled && {
        '&:hover': { backgroundColor: $checked ? tokens.ColorSurfaceSelectedHover : tokens.ColorSurfaceHover },
        '&:active': { backgroundColor: $checked ? tokens.ColorSurfaceSelectedPressed : tokens.ColorSurfacePressed },
        '&:focus-within': {
            outline: `${tokens.SizingBorderM} solid ${tokens.ColorBorderFocused}`,
            outlineOffset: tokens.SizingSpacing025,
        },
    }),
}))
